import InfinityScrollControlled, {
  InfinityProps,
} from '@/components/InfinityScrollControlled';
import {
  performCommercialVisibleChannels,
  projectAllowed16sSequencingType,
  researchAnalysisAllowedSampleType,
} from '@/constants';
import { AnalysisStatus, ChannelName } from '@/enums';
import { useGetChannels, useParseQueryString, useQueryNavigate } from '@/hooks';
import { AnalysisParams, ChannelEntity } from '@/interfaces';
import { CreateInitDataPayload } from '@/interfaces/analysis';
import { useGetInfiniteProjects } from '@/queries';
import { ProjectParams } from '@/types';
import { generateOptions, useToggle } from '@/utils';
import {
  ControlledInput,
  CustomButton,
  CustomFilter,
  Icon,
  PAGE_DEFAULT,
} from '@amili-v3/amili-v3-design';
import { Divider, Form } from 'antd';
import dayjs from 'dayjs';
import { Formik, FormikProps } from 'formik';
import { omit } from 'lodash';
import { FC, memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { SequencingDatePickerWrapper } from '../../Sequencing/SequencingManagement/styles';
import { FilterBody, FilterContent, FilterHeader } from '../../styles';
import { AnalysisTabPane } from '../home/AnalysisTabs';

const defaultInitialValues: AnalysisParams = {
  dateOfCreationFrom: undefined,
  dateOfCreationTo: undefined,
  dateOfSendAnalysisFrom: undefined,
  dateOfSendAnalysisTo: undefined,
  projectId: undefined,
  status: undefined,
};

const transformedFormValues = (value: AnalysisParams) => {
  const newPayload: CreateInitDataPayload = {};
  if (value.dateOfCreationFrom)
    newPayload.dateOfCreationFrom = dayjs(value?.dateOfCreationFrom)
      .startOf('day')
      .format();
  if (value.dateOfCreationTo)
    newPayload.dateOfCreationTo = dayjs(value?.dateOfCreationTo)
      .endOf('day')
      .format();
  if (value.dateOfSendAnalysisFrom)
    newPayload.dateOfSendAnalysisFrom = dayjs(value?.dateOfSendAnalysisFrom)
      .startOf('day')
      .format();
  if (value.dateOfSendAnalysisTo)
    newPayload.dateOfSendAnalysisTo = dayjs(value?.dateOfSendAnalysisTo)
      .endOf('day')
      .format();
  if (value.projectId) newPayload.projectId = value.projectId;
  if (value.status) newPayload.status = value.status as AnalysisStatus;
  return { ...newPayload, page: PAGE_DEFAULT };
};

const AnalysisFilter: FC = () => {
  const { t } = useTranslation();
  const [open, toggleOpen] = useToggle();
  const [rotate, setRotate] = useState(false);
  const handleNavigate = useQueryNavigate<AnalysisParams>();
  const { pathname } = useLocation();

  const formRef = useRef<FormikProps<AnalysisParams>>(null);
  const qsParams = useParseQueryString<AnalysisParams>();

  const { channels } = useGetChannels();

  const [searchProjectValue, setSearchProjectValue] = useState('');

  const initialValues: AnalysisParams = useMemo(
    () => ({
      ...defaultInitialValues,
      ...qsParams,
    }),
    [qsParams],
  );

  const projectQueryParams = useMemo<ProjectParams>(() => {
    const currentActiveTab = pathname.split('/').at(-1) ?? '';
    const isResearchAnalysisTab = currentActiveTab === AnalysisTabPane.RESEARCH;

    const externalChannelId =
      channels.find((c: ChannelEntity) => c.name === ChannelName.EXTERNAL)
        ?.id ?? '';
    const allowedCommercialChannelIds = channels?.reduce(
      (acc: string[], channel) => {
        if (performCommercialVisibleChannels.includes(channel.name)) {
          acc.push(channel.id);
        }
        return acc;
      },
      [],
    );

    const generalParams: ProjectParams = {
      orderField: 'name',
      order: 'ASC',
      q: searchProjectValue || undefined,
      sequencingType: projectAllowed16sSequencingType,
      priorityIds: qsParams?.projectId ? [qsParams.projectId] : undefined,
    };
    if (isResearchAnalysisTab) {
      return {
        ...generalParams,
        externalSampleTypes: [researchAnalysisAllowedSampleType],
        channelIds: [externalChannelId],
      };
    }
    return {
      ...generalParams,
      channelIds: allowedCommercialChannelIds,
      isPerformAnalysisBatch: true,
    };
  }, [pathname, channels, searchProjectValue, qsParams?.projectId]);
  const {
    flatData: sixteenSSequencingTypeProjects,
    isLoading: isLoadingProjects,
    isFetching: isFetchingProjects,
    hasNextPage,
    fetchNextPage,
  } = useGetInfiniteProjects({
    params: projectQueryParams,
  });
  const projectsQueryResult: InfinityProps['infinityQueryResult'] = {
    isLoading: isLoadingProjects,
    isFetching: isFetchingProjects,
    hasNextPage,
    fetchNextPage,
  };
  const projectOptions = generateOptions(sixteenSSequencingTypeProjects);

  const onResetFilter = () => {
    if (!formRef.current) return;
    formRef?.current?.setValues(defaultInitialValues);
  };

  const countQuantity = useMemo(() => {
    const filterValue: AnalysisParams = {
      dateOfCreationFrom: qsParams?.dateOfCreationFrom,
      dateOfCreationTo: qsParams?.dateOfCreationTo,
      dateOfSendAnalysisFrom: qsParams.dateOfSendAnalysisFrom,
      dateOfSendAnalysisTo: qsParams.dateOfSendAnalysisTo,
      projectId: qsParams?.projectId,
      status: qsParams?.status,
    };

    return Object.values(filterValue).filter((val: string) => !!val).length;
  }, [qsParams]);

  const onSubmit = useCallback(
    (value: AnalysisParams) => {
      const newValues = transformedFormValues(value);

      const missingKeys = omit(initialValues, Object.keys(newValues));

      handleNavigate(
        newValues,
        Object.keys(missingKeys) as (keyof AnalysisParams)[],
      );
      toggleOpen();
    },
    [handleNavigate, initialValues, toggleOpen],
  );

  return (
    <CustomFilter
      open={open}
      onOpenChange={toggleOpen}
      quantity={countQuantity}
      destroyPopupOnHide
      dropdownRender={() => (
        <FilterContent onClick={e => e.stopPropagation()}>
          <FilterHeader>
            {t('common.filters')}
            <CustomButton
              type="white"
              onClick={() => {
                onResetFilter();
                setRotate(true);
                setTimeout(() => {
                  setRotate(false);
                }, 300);
              }}
              htmlType="submit"
              form="filterForm"
            >
              {<Icon name={'refresh'} width={15} rotateLeft={rotate} />}
            </CustomButton>
          </FilterHeader>
          <Divider />
          <FilterBody>
            <Formik
              innerRef={formRef}
              initialValues={initialValues}
              onSubmit={onSubmit}
            >
              {({ handleSubmit }) => {
                return (
                  <FormWrapper
                    layout="vertical"
                    onFinish={handleSubmit as any}
                    id="filterForm"
                  >
                    <SequencingDatePickerWrapper>
                      <ControlledInput
                        inputType="datePicker"
                        name={'dateOfCreationFrom'}
                        label={t('common.dateCreated')}
                        formatFullDate
                        allowClear
                      />
                      <div className="divider" />
                      <ControlledInput
                        inputType="datePicker"
                        name={'dateOfCreationTo'}
                        label={t('common.dateCreated')}
                        formatFullDate
                        hideLabel
                        allowClear
                      />
                    </SequencingDatePickerWrapper>
                    <SequencingDatePickerWrapper>
                      <ControlledInput
                        inputType="datePicker"
                        name={'dateOfSendAnalysisFrom'}
                        label={t('analysis.sendForAnalysis')}
                        formatFullDate
                        allowClear
                      />
                      <div className="divider" />
                      <ControlledInput
                        inputType="datePicker"
                        name={'dateOfSendAnalysisTo'}
                        label={t('analysis.sendForAnalysis')}
                        formatFullDate
                        hideLabel
                        allowClear
                      />
                    </SequencingDatePickerWrapper>
                    <InfinityScrollControlled
                      name="projectId"
                      label={t('project.projectName')}
                      options={projectOptions}
                      isControlledConfig={false}
                      aria-label="Project select"
                      allowClear
                      infinityQueryResult={projectsQueryResult}
                      filterValue={searchProjectValue}
                      setFilterValue={setSearchProjectValue}
                    />
                    <ControlledInput
                      name="status"
                      inputType="select"
                      label={t('common.status')}
                      options={generateOptions(Object.values(AnalysisStatus), {
                        translationPath: 'analysis.status',
                      })}
                      allowClear
                      showSearch
                    />
                    <CustomButton
                      size="large"
                      width={'100%'}
                      htmlType="submit"
                      form="filterForm"
                    >
                      {t('common.apply')}
                    </CustomButton>
                  </FormWrapper>
                );
              }}
            </Formik>
          </FilterBody>
        </FilterContent>
      )}
    />
  );
};

export default memo(AnalysisFilter);

export const FormWrapper = styled(Form)`
  .ant-picker {
    flex: 1;
  }
  .ant-row {
    flex-wrap: nowrap;
  }
`;
